<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { useSidebarStyleStore } from "@/stores/sidebarStyle";
const sidebarStyleStore = useSidebarStyleStore();

const margin = 20; // 间距
const buttonWidth = 100;
const buttonHeight = 50;

const position = ref({
  x: window.innerWidth - buttonWidth - margin,
  y: window.innerHeight - buttonHeight - margin,
});
const viewportWidth = ref(window.innerWidth);
const viewportHeight = ref(window.innerHeight);
let isDragging = false;
let dragOffset = { x: 0, y: 0 };
// 拖拽位移超过阈值视为拖动而非点击，抑制 click 切换（避免拖动结束误触隐藏）
let dragStart = { x: 0, y: 0 };
let dragMoved = false;

const startDrag = (event: MouseEvent | TouchEvent) => {
  isDragging = true;
  dragMoved = false;
  const clientX = (event as MouseEvent).clientX ?? (event as TouchEvent).touches[0].clientX;
  const clientY = (event as MouseEvent).clientY ?? (event as TouchEvent).touches[0].clientY;
  dragStart = { x: clientX, y: clientY };
  dragOffset = {
    x: clientX - position.value.x,
    y: clientY - position.value.y,
  };

  document.addEventListener("mousemove", onDrag);
  document.addEventListener("touchmove", onDrag);
  document.addEventListener("mouseup", stopDrag);
  document.addEventListener("touchend", stopDrag);
};

const onDrag = (event: MouseEvent | TouchEvent) => {
  if (!isDragging) return;
  const clientX = (event as MouseEvent).clientX ?? (event as TouchEvent).touches[0].clientX;
  const clientY = (event as MouseEvent).clientY ?? (event as TouchEvent).touches[0].clientY;

  if (Math.abs(clientX - dragStart.x) > 5 || Math.abs(clientY - dragStart.y) > 5) {
    dragMoved = true;
  }

  let newX = clientX - dragOffset.x;
  let newY = clientY - dragOffset.y;

  // Constrain within viewport
  newX = Math.max(0, Math.min(newX, viewportWidth.value - buttonWidth));
  newY = Math.max(0, Math.min(newY, viewportHeight.value - buttonHeight));

  position.value = { x: newX, y: newY };
};

const stopDrag = () => {
  isDragging = false;
  document.removeEventListener("mousemove", onDrag);
  document.removeEventListener("touchmove", onDrag);
  document.removeEventListener("mouseup", stopDrag);
  document.removeEventListener("touchend", stopDrag);
};

const onResize = () => {
  viewportWidth.value = window.innerWidth;
  viewportHeight.value = window.innerHeight;
  // Update position to maintain the margin
  position.value = {
    x: Math.min(position.value.x, viewportWidth.value - buttonWidth - margin),
    y: Math.min(position.value.y, viewportHeight.value - buttonHeight - margin),
  };
};

// 桌面端侧边栏可见时显示浮动按钮（组件本身已被 App.vue 限制在 !isMobile 下渲染）。
// 此前 buttonShow 初始为 false 且桌面分支直接 return，按钮从不出现，侧边栏没有关闭入口。
// 改为响应式跟随 store：可见时显示「隐藏」，收起后按钮保留并显示「显示」用于恢复。
const buttonShow = computed(() => sidebarStyleStore.showSidebar);
const forceHiddenSidebar = computed(() => sidebarStyleStore.forceHiddenSidebar);

const onClick = () => {
  if (dragMoved) {
    return;
  }
  sidebarStyleStore.setForceHiddenSidebar(!sidebarStyleStore.forceHiddenSidebar);
};

onMounted(() => {
  window.addEventListener("resize", onResize);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", onResize);
});
</script>

<template>
  <t-button
    v-if="buttonShow"
    class="draggable-button"
    :style="{ top: `${position.y}px`, left: `${position.x}px` }"
    variant="dashed"
    theme="primary"
    @mousedown="startDrag"
    @touchstart="startDrag"
    @click="onClick"
  >
    {{ forceHiddenSidebar ? "显示" : "隐藏" }}
  </t-button>
</template>

<style scoped>
.draggable-button {
  position: fixed; /* Changed to fixed */
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  user-select: none;
  touch-action: none;
  z-index: 2000;
}
</style>
